import { Button, Input, Textarea, MultiSelect } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation, useQuery } from "@tanstack/react-query";
import Common from "@utilities/common";
import { Form, Formik, FormikHelpers } from "formik";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IFormData } from "./types";
import { Schema } from "./validations";
import Editor from "@components/editor";

const FormContainer: FC = () => {
    const [initialValues] = useState<IFormData>({
        title: "",
        brief: "",
        url: "",
        body: "",
        tags: [],
        seoDescription: "",
        seoKeywords: "",
    });
    const [tagsQuery, setTagsQuery] = useState("");

    const Navigate = useNavigate();

    const { data: tagsData } = useQuery(
        [
            "getTags",
            {
                query: tagsQuery,
            },
        ],
        () => Common.api.tags.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { mutate, isLoading } = useMutation(
        ({
            title,
            brief,
            url,
            body,
            tags,
            seoDescription,
            seoKeywords,
        }: IFormData) =>
            Common.api.articles.Create({
                title,
                brief,
                url,
                tags,
                ...(body ? { body } : {}),
                ...(seoDescription ? { seoDescription } : {}),
                ...(seoKeywords ? { seoKeywords } : {}),
            }),
        {
            onSuccess: () => {
                Navigate(RoutesInstance.articles);
            },
        },
    );

    const handleSubmit = async (
        values: IFormData,
        formik: FormikHelpers<IFormData>,
    ) => {
        mutate(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Schema}
            validateOnBlur={true}
            validateOnChange={true}>
            {({ values, errors, handleChange, handleBlur, isValid }) => (
                <Form>
                    <div className="mb-4">
                        <Input.Wrapper
                            id="title"
                            label="عنوان"
                            required
                            error={errors.title}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="title"
                                name="title"
                                type="text"
                                placeholder="عنوان"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.title}
                                invalid={!!errors.title}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="brief"
                            name="brief"
                            placeholder="توضیح کوتاه"
                            label="توضیح کوتاه"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            required
                            error={errors.brief}
                            value={values.brief}
                        />
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="url"
                            label="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                            required
                            error={errors.url}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="url"
                                name="url"
                                type="text"
                                placeholder="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.url}
                                invalid={!!errors.url}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                tagsData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.tags.map((item) => item.toString())}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "tags",
                                        name: "tags",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="برچسب ها"
                            placeholder="انتخاب برچسب"
                            searchable
                            searchValue={tagsQuery}
                            onSearchChange={(query) => setTagsQuery(query)}
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-4">
                        <Editor
                            value={values.body}
                            handleChange={(value: string) =>
                                handleChange({
                                    target: {
                                        id: "body",
                                        name: "body",
                                        value,
                                    },
                                })
                            }
                        />
                    </div>

                    <div className="mb-4">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="seoDescription"
                            name="seoDescription"
                            placeholder="توضیحات سئو"
                            label="توضیحات سئو"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            error={errors.seoDescription}
                            value={values.seoDescription}
                        />
                    </div>

                    <div className="mb-10">
                        <Input.Wrapper
                            id="seoKeywords"
                            label="کلمات کلیدی سئو"
                            error={errors.seoKeywords}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoKeywords"
                                name="seoKeywords"
                                type="text"
                                placeholder="کلمات کلیدی سئو"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoKeywords}
                                invalid={!!errors.seoKeywords}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="text-center">
                        <Button
                            variant="filled"
                            className="transition-all duration-300 px-16"
                            color="blue"
                            radius={9999}
                            type="submit"
                            disabled={!isValid || isLoading}
                            loading={isLoading}>
                            ثبت
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FormContainer;
